import { ColDef, IRowNode, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import orderBy from "lodash.orderby";
import { useEffect, useMemo, useRef } from "react";

import { IgTableWrapper, StripeColor } from "@intelligems/ig-theme";
import {
  StripeApiCouponDto,
  StripeApiPriceTierDto,
} from "@intelligems/ig-types";
import { hexToRgbA } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";
import {
  CurrencyFormat,
  DecimalFormat,
} from "@src/features/admin/components/utils";
import { theme } from "@src/theme";

interface SubscriptionsTableProps {
  tiers: StripeApiPriceTierDto[];
  coupon: StripeApiCouponDto | null | undefined;
  quantity: number;
}

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
});

export const PriceTierTable = ({
  tiers,
  quantity,
  coupon,
}: SubscriptionsTableProps) => {
  const gridRef = useRef<AgGridReact>(null);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      cellStyle: { fontSize: "13px" },
    };
  }, []);

  const columnDefs: ColDef<StripeApiPriceTierDto & { "": "" }>[] =
    useMemo(() => {
      const cols: ColDef<StripeApiPriceTierDto & { "": "" }>[] = [
        {
          headerName: "Quantity",
          field: "description",
          minWidth: 300,
          cellStyle: { justifyContent: "start" },
        },
        {
          headerName: "Unit Price",
          field: "unitAmount",
          width: 30,
          valueFormatter: (params) => {
            return CurrencyFormat.format(
              params.data?.unitAmount ? params.data.unitAmount / 100 : 0
            );
          },
        },
        {
          headerName: "Flat Amount",
          field: "flatAmount",
          width: 30,
          valueFormatter: (params) => {
            return CurrencyFormat.format(
              params.data?.flatAmount ? params.data.flatAmount / 100 : 0
            );
          },
        },
      ];

      if (coupon) {
        cols.push({
          headerName: "Discounted",
          field: "",
          width: 100,
          valueFormatter: (params) => {
            if (!coupon) {
              return "";
            }
            const amount = params.data?.flatAmount
              ? params.data.flatAmount / 100
              : 0;

            const discountedAmount = coupon.percentOff
              ? amount - amount * (coupon.percentOff / 100)
              : amount - (coupon.amountOff || 0) / 100 || 0;

            return CurrencyFormat.format(discountedAmount);
          },
        });
      }

      return cols;
    }, [coupon]);

  const highlightRow = () => {
    gridRef?.current?.api?.forEachNode(
      (node: IRowNode<StripeApiPriceTierDto & { from: number }>) => {
        if (!quantity) {
          node.setSelected(false);
        } else if (
          node.data &&
          node.data.from !== undefined &&
          node.data.upTo &&
          quantity >= node.data.from &&
          quantity < node.data.upTo
        ) {
          node.setSelected(true);
        } else if (
          node.data &&
          node.data.from &&
          !node.data?.upTo &&
          quantity >= node.data.from
        ) {
          node.setSelected(true);
        }
      }
    );
  };

  useEffect(() => {
    highlightRow();
  }, [quantity]);

  const data = useMemo(() => {
    const orderedTiers = orderBy(tiers, "upTo", "asc");
    return orderedTiers.map((tier, index) => ({
      ...tier,
      from: index === 0 ? 0 : orderedTiers[index - 1].upTo!,
      description: `If the total quantity is ${index === 0 ? 0 : DecimalFormat.format(orderedTiers[index - 1].upTo!)}${index === orderedTiers.length - 1 ? " or more " : " "}${index === orderedTiers.length - 1 ? "" : `to ${DecimalFormat.format(tier.upTo!)}`}`,
    }));
  }, [tiers]);

  return (
    <Grid container>
      <Grid
        item
        sx={{
          marginTop: "10px",
          width: "100%",
          "& .ag-row-selected:before": {
            backgroundColor: hexToRgbA(StripeColor, 0.2),
          },
          "& .ag-theme-alpine": {
            height: "unset !important",
            minHeight: "unset !important",
          },
        }}
      >
        <IgTableWrapper>
          <AgGridReact<StripeApiPriceTierDto>
            theme={myTheme}
            ref={gridRef}
            rowData={data}
            defaultColDef={defaultColDef}
            domLayout={"autoHeight"}
            columnDefs={columnDefs}
            headerHeight={25}
            rowHeight={30}
            rowSelection={"single"}
            suppressCellFocus={true}
            suppressRowHoverHighlight={true}
            onGridReady={() => highlightRow()}
          />
        </IgTableWrapper>
      </Grid>
    </Grid>
  );
};
